import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import HeadTitle from '../../components/HeadTitle';
import Loader from '../../components/common/Loader/Loader';
import { getRequest } from '../../utils/axiosUtils';

/**
 * Daily Actives & Gender Balance — a full page (route `/daily-actives`), rendered inside
 * the admin Layout like Channel Attribution / Onboarding Drop-off (no iframe).
 *
 * Data: GET /admin/attribution/daily-actives?from&to&platform returns, over the IST window,
 * `days` (distinct non-bot users active per day, split women/men) — gap-filled so a day
 * with no actives is an explicit 0 — plus `hours` (the hour-of-day profile) and `online`
 * (real presence). A "daily active" is a distinct user with at least one socket-connect
 * event that day; that's the same `user_logs` source the admin's realtime/weekly cards use.
 *
 * Online-now is polled separately (its own route, so the two calls don't abort each other)
 * and is NOT simulated — bots are excluded, which matters a lot here: ~9.7k bots carry
 * isOnLine:true from the bot rotation, so an unfiltered count would be ~100x the truth.
 * Clicking the KPI opens the Online Right Now page (route `/online-now`) in a new tab — the
 * same pattern as the Ghost Users card → `/onboarding-dropoff`. It used to open an inline
 * modal; that list now lives on the page, which carries the platform filter across so it
 * opens describing the same people this KPI counted.
 *
 * Scoped under `.favor-da` so its styles never touch the admin's global CSS. Fonts use the
 * admin app's own families (ivypresto-headline serif + indivisible sans).
 */

// The backend's calendar is IST, so ours must be too. `toISOString()` would give the UTC
// day: between midnight and 05:30 IST that is still YESTERDAY, so the page would ask for a
// window ending a day early and today's bar would silently vanish. Same trick the server
// uses for todayIst. Day arithmetic stays in UTC-noon so a DST/offset shift can't move it.
const TODAY = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // YYYY-MM-DD
const shiftDays = (isoStr, n) => {
  const d = new Date(`${isoStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

// First presence event in user_logs — socket status logging starts here, so no earlier day
// can have data. Keep in sync with AttributionController.DAILY_ACTIVES_CUTOFF.
const CUTOFF = '2025-10-13';
const clampDay = (d) => (d < CUTOFF ? CUTOFF : d > TODAY ? TODAY : d);

const ONLINE_POLL_MS = 45000;

function rangeWindow(range, from, to) {
  switch (range) {
    case 'yesterday': { const y = shiftDays(TODAY, -1); return { from: y, to: y }; }
    case '30d': return { from: shiftDays(TODAY, -29), to: TODAY };
    case 'launch': return { from: undefined, to: TODAY };
    case 'custom': return { from, to };
    case '7d':
    default: return { from: shiftDays(TODAY, -6), to: TODAY };
  }
}

const PERIODS = [['yesterday', 'Yesterday'], ['7d', 'Last 7 days'], ['30d', 'Last 30 days'], ['launch', 'Since launch'], ['custom', 'Custom']];
const PLATFORMS = [['all', 'All'], ['app', 'App'], ['web', 'Web']];
const PERIOD_LABEL = { yesterday: 'Yesterday', '7d': 'Last 7 days', '30d': 'Last 30 days', launch: 'Since launch' };
const PLATFORM_LABEL = { all: 'all platforms', app: 'app', web: 'web' };

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
// Parse a YYYY-MM-DD as a LOCAL date. `new Date('2026-07-13')` is parsed as UTC and can
// land on the previous day in a negative-offset browser, shifting every weekday label.
const parseDay = (d) => { const [y, m, dd] = d.split('-').map(Number); return new Date(y, m - 1, dd); };

// Men per 100 women — the report's balance metric. 0 women is "no ratio", not infinity.
const ratioOf = (w, m) => (w ? Math.round((m / w) * 100) : 0);
const ratioTag = (r) => (r <= 115 ? { cls: 'ok', txt: 'balanced' } : r <= 145 ? { cls: 'mid', txt: 'male-leaning' } : { cls: 'bad', txt: 'heavily skewed' });

const h12 = (h) => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? 'a' : 'p'}`;
const hRange = (h) => `${h12(h)}–${h12((h + 1) % 24)}`;

const Pills = ({ items, value, onChange, variant }) => (
  <div className="da-pills">
    {items.map(([k, label]) => (
      <button key={k} type="button" className={`da-pill${value === k ? ` on ${variant || ''}` : ''}`} onClick={() => onChange(k)}>{label}</button>
    ))}
  </div>
);

// A KPI is a plain tile unless it's given an onClick, in which case it becomes a real
// <button> — keyboard-focusable and Enter/Space-activatable — rather than a div that only
// a mouse can reach.
const Kpi = ({ k, v, d, cls, unit, live, onClick, cta }) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} className={`kpi ${cls || ''}${onClick ? ' tap' : ''}`} onClick={onClick}>
      <div className="k">{live ? <span className="pulse" /> : null}{k}</div>
      <div className="v serif">{v}{unit ? <small>{unit}</small> : null}</div>
      <div className="d">{d}</div>
      {cta ? <span className="kcta">{cta} →</span> : null}
    </Tag>
  );
};
const Callout = ({ t, cls, children }) => (
  <div className={`callout ${cls || ''}`}><div className="ct">{t}</div><div className="cv">{children}</div></div>
);

const DailyActives = () => {
  const [range, setRange] = useState('7d');
  const [from, setFrom] = useState(clampDay(shiftDays(TODAY, -6)));
  const [to, setTo] = useState(TODAY);
  const [platform, setPlatform] = useState('all');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);
  const [online, setOnline] = useState(null);

  const win = useMemo(() => rangeWindow(range, from, to), [range, from, to]);

  // "Online now" card → the full-page roster, in a new tab.
  //
  // The platform filter deliberately does NOT ride along: the Online Now page has no
  // platform control (design decision — its mock doesn't have one), so it always lists
  // every platform. Passing a param the page ignores would be worse than not passing it.
  // Consequence to know about: opening this from a platform-filtered KPI lands on a page
  // listing MORE people than the number just clicked.
  const openOnline = useCallback(() => {
    window.open('/online-now', '_blank', 'noopener,noreferrer');
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true); setError(false);
    (async () => {
      try {
        const params = {};
        if (win.from) params.from = win.from;
        if (win.to) params.to = win.to;
        if (platform !== 'all') params.platform = platform;
        const res = await getRequest({ url: '/admin/attribution/daily-actives', params });
        if (!active) return;
        // getRequest returns null when the call was aborted by a superseding request to the
        // same path. A cancel is NOT a failure — ignore it so we don't flash an error.
        if (!res) return;
        const body = res?.data;
        const payload = Array.isArray(body?.days) ? body : body?.data;
        if (payload && Array.isArray(payload.days) && Array.isArray(payload.hours)) {
          setData(payload);
          if (payload.online) setOnline(payload.online);
        } else setError(true);
      } catch (e) { if (active) setError(true); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [win, platform]);

  // Presence poll — its own route (not ?online=1 on the report path), because getRequest
  // aborts in-flight calls keyed on the URL path and would otherwise cancel the report
  // fetch. Only the platform filter applies; the period is irrelevant to "right now".
  const pollOnline = useCallback(async () => {
    try {
      const params = platform !== 'all' ? { platform } : {};
      const res = await getRequest({ url: '/admin/attribution/actives-online', params });
      const body = res?.data;
      const payload = body?.online ? body : body?.data;
      if (payload?.online) setOnline(payload.online);
    } catch (e) { /* transient — keep showing the last known count */ }
  }, [platform]);

  useEffect(() => {
    const id = setInterval(pollOnline, ONLINE_POLL_MS);
    return () => clearInterval(id);
  }, [pollOnline]);

  // Everything the page renders, derived once from the window's days.
  const stats = useMemo(() => {
    const days = data?.days || [];
    if (!days.length) return null;
    const rows = days.map((d) => {
      const dt = parseDay(d.date);
      const dow = dt.getDay();
      return {
        ...d,
        dow: DOW[dow],
        wkend: dow === 0 || dow === 6,
        lab: `${MON[dt.getMonth()]} ${dt.getDate()}`,
        r: ratioOf(d.women, d.men),
      };
    });
    const n = rows.length;
    const sumW = rows.reduce((a, r) => a + r.women, 0);
    const sumM = rows.reduce((a, r) => a + r.men, 0);
    const sumT = rows.reduce((a, r) => a + r.total, 0);

    const peak = rows.reduce((a, r) => (!a || r.total > a.total ? r : a), null);
    // Balance extremes are only meaningful on days that actually had women — a 0-women day
    // has no ratio, and an empty day has nothing to say about balance.
    const rated = rows.filter((r) => r.women > 0);
    const dev = (r) => Math.abs(r.r - 100);
    const best = rated.reduce((a, r) => (!a || dev(r) < dev(a) ? r : a), null);
    const skew = rated.reduce((a, r) => (!a || dev(r) > dev(a) ? r : a), null);

    const wkend = rows.filter((r) => r.wkend);
    const wkday = rows.filter((r) => !r.wkend);
    const avg = (arr) => (arr.length ? Math.round(arr.reduce((a, r) => a + r.total, 0) / arr.length) : 0);

    return {
      rows,
      n,
      avgT: Math.round(sumT / n),
      avgW: Math.round(sumW / n),
      avgM: Math.round(sumM / n),
      ratio: ratioOf(sumW, sumM),
      wPct: sumT ? Math.round((sumW / sumT) * 100) : 0,
      peak,
      best,
      skew,
      maxT: Math.max(...rows.map((r) => r.total), 0),
      maxGender: Math.max(...rows.map((r) => Math.max(r.women, r.men)), 0),
      wkendAvg: avg(wkend),
      wkdayAvg: avg(wkday),
      hasBoth: wkend.length > 0 && wkday.length > 0,
    };
  }, [data]);

  // Hour-of-day profile. The API returns the SUM over days of distinct users active in that
  // hour, so divide by the day count to get the average day's shape.
  const hourly = useMemo(() => {
    const hours = data?.hours || [];
    if (!stats || !hours.length) return null;
    const n = stats.n;
    // Round each series, then TOTAL THE ROUNDED PARTS — rounding total independently lets it
    // disagree with its own stack (9 women + 11 men shown under a bar labelled 21).
    const rows = hours.map((h) => {
      const women = Math.round(h.women / n);
      const men = Math.round(h.men / n);
      const unknown = Math.round((h.unknown || 0) / n);
      return { hour: h.hour, women, men, unknown, total: women + men + unknown };
    });
    const max = Math.max(...rows.map((r) => r.total), 0);
    const pick = (key) => rows.reduce((a, r) => (r[key] > a[key] ? r : a), rows[0]);
    const peak = pick('total');
    const quiet = rows.reduce((a, r) => (r.total < a.total ? r : a), rows[0]);
    // "Most skewed hour" only over hours with real traffic — a 3am hour with 2 users would
    // otherwise always win on ratio noise.
    const floor = Math.max(5, max * 0.15);
    const busy = rows.filter((r) => r.total >= floor && r.women > 0);
    const skew = busy.reduce((a, r) => (!a || Math.abs(ratioOf(r.women, r.men) - 100) > Math.abs(ratioOf(a.women, a.men) - 100) ? r : a), null);
    return { rows, max, peak, quiet, skew, peakW: pick('women'), peakM: pick('men') };
  }, [data, stats]);

  const periodLabel = range === 'custom' ? `${from} → ${to}` : PERIOD_LABEL[range];

  const exportCsv = () => {
    if (!stats) return;
    const head = ['Date', 'Weekday', 'Women', 'Men', 'Unknown', 'Total', 'Men per 100 women'];
    const rows = stats.rows.map((r) => [r.date, r.dow, r.women, r.men, r.unknown, r.total, r.women ? r.r : '']);
    const csv = [head, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `daily-actives-${range}-${TODAY}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const setPeriod = (v) => {
    setRange(v);
    if (v === 'custom') { setFrom(clampDay(from)); setTo(clampDay(to)); }
  };

  return (
    <Box sx={{ pb: 4 }}>
      <style>{STYLES}</style>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <HeadTitle
          imageSrc="/assets/images/admin-panel/analytics.png"
          text="Daily Actives"
          description="How many members use Favor each day — and the male-to-female split behind the number"
        />
      </Stack>

      <div className="favor-da">
        <div className="filters">
          <div className="fg"><span className="lab">Period</span>
            <Pills items={PERIODS} value={range} onChange={setPeriod} variant="red" />
            {range === 'custom' && (
              <span className="daterange">
                <input type="date" min={CUTOFF} max={TODAY} value={from} onChange={(e) => { const v = clampDay(e.target.value); setFrom(v); if (v > to) setTo(v); }} />
                <span className="dr-sep">to</span>
                <input type="date" min={CUTOFF} max={TODAY} value={to} onChange={(e) => { const v = clampDay(e.target.value); setTo(v); if (v < from) setFrom(v); }} />
              </span>
            )}
          </div>
          <div className="fg"><span className="lab">Platform</span>
            <Pills items={PLATFORMS} value={platform} onChange={setPlatform} variant="black" />
          </div>
          <button type="button" className="da-export" onClick={exportCsv} disabled={!stats}>
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M8 2v8m0 0L5 7m3 3l3-3M3 12h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Export
          </button>
        </div>

        <p className="note">
          Showing: <b>{periodLabel} · {PLATFORM_LABEL[platform]}</b> · a daily active is a distinct member who opened the
          app or site that day (IST). Bots excluded.
          <span className="flag">◆ App/web = install-token heuristic · Presence logging starts 13 Oct 2025</span>
        </p>

        {loading ? (
          <div className="da-center"><Loader /></div>
        ) : error || !stats ? (
          <div className="note" style={{ padding: '40px 0' }}>Couldn’t load the activity data. Try another period.</div>
        ) : (
          <>
            {/* KPIs */}
            <div className="kpis">
              {/* Only clickable once we know someone IS online — a panel that can only ever
                  say "nobody" isn't worth a click, and there's nothing to preview at 0. */}
              <Kpi
                live
                cls="live"
                k="Online now"
                v={online ? fmt(online.total) : '—'}
                d={online ? `${fmt(online.men)} men · ${fmt(online.women)} women` : 'checking…'}
                onClick={online?.total ? openOnline : undefined}
                cta={online?.total ? 'See who’s online ↗' : undefined}
              />
              <Kpi
                k="Daily actives"
                v={fmt(stats.avgT)}
                unit="avg/day"
                d={stats.peak ? `Peak ${fmt(stats.peak.total)} on ${stats.peak.lab}` : '—'}
              />
              <Kpi cls="w" k="Women / day" v={fmt(stats.avgW)} d={`${stats.wPct}% of daily actives`} />
              <Kpi cls="m" k="Men / day" v={fmt(stats.avgM)} d={`${100 - stats.wPct}% of daily actives`} />
              <div className="kpi">
                <div className="k">M : F ratio</div>
                <div className="v serif">{(stats.ratio / 100).toFixed(2)}<small>: 1</small></div>
                <div className="d">{stats.ratio} men per 100 women</div>
                <span className={`rtag ${ratioTag(stats.ratio).cls}`}>{ratioTag(stats.ratio).txt}</span>
              </div>
            </div>

            {/* ACTIVES PER DAY */}
            <div className="sec">
              <div className="sec-h">
                <h2>Actives per day</h2>
                <span className="hint">Women below, men above — counts inside each block, or just above it when the block is too short. Weekend labels in gold.</span>
              </div>
              <div className="legend">
                <span className="lg"><span className="dotw" />Women</span>
                <span className="lg"><span className="dotm" />Men</span>
                <span className="lg faint">{stats.peak ? `Tallest bar = ${fmt(stats.maxT)} actives (${stats.peak.lab})` : ''}</span>
              </div>
              <DayChart stats={stats} />
            </div>

            {/* BY HOUR */}
            {hourly && (
              <div className="sec">
                <div className="sec-h">
                  <h2>By hour of day</h2>
                  <span className="hint">
                    {stats.n === 1
                      ? `Every hour of ${stats.rows[0].lab} (${stats.rows[0].dow}). Gold column = peak hour; counts inside blocks.`
                      : `Average per hour across ${stats.n} days. Gold column = peak hour; counts inside blocks.`}
                  </span>
                </div>
                <div className="legend">
                  <span className="lg"><span className="dotw" />Women</span>
                  <span className="lg"><span className="dotm" />Men</span>
                  <span className="lg faint">Peak hour {hRange(hourly.peak.hour)} · {fmt(hourly.max)}{stats.n > 1 ? ' avg' : ''} actives</span>
                </div>
                <HourChart hourly={hourly} avg={stats.n > 1} />
                <div className="ceta">
                  <Callout t="Peak hour" cls="good">
                    <b>{hRange(hourly.peak.hour)}</b> — {fmt(hourly.max)}{stats.n > 1 ? ' avg' : ''} actives ({fmt(hourly.peak.men)} M · {fmt(hourly.peak.women)} W).
                  </Callout>
                  <Callout t="Gender peaks">
                    Women peak at <b>{hRange(hourly.peakW.hour)}</b>, men at <b>{hRange(hourly.peakM.hour)}</b>
                    {hourly.peakW.hour !== hourly.peakM.hour ? ' — stagger pushes/digests accordingly.' : ' — same window; one prime slot.'}
                  </Callout>
                  {hourly.skew && (
                    <Callout t="Most skewed hour" cls="leak">
                      <b>{hRange(hourly.skew.hour)}</b> — {ratioOf(hourly.skew.women, hourly.skew.men)} men per 100 women (busy hours only).
                    </Callout>
                  )}
                  <Callout t="Quietest hour">
                    <b>{hRange(hourly.quiet.hour)}</b> — {fmt(hourly.quiet.total)}{stats.n > 1 ? ' avg' : ''} actives. Maintenance / deploy window.
                  </Callout>
                </div>
              </div>
            )}

            {/* DAY BY DAY */}
            <div className="sec">
              <div className="sec-h">
                <h2>Day by day</h2>
                <span className="hint">Newest first. Ratio = men per 100 women. Green row = most balanced day, red = most skewed.</span>
              </div>
              <DayRows stats={stats} />
              <div className="ceta">
                {stats.peak && (
                  <Callout t="Busiest day" cls="good">
                    <b>{stats.peak.lab}</b> — {fmt(stats.peak.total)} actives ({fmt(stats.peak.men)} M · {fmt(stats.peak.women)} W).
                  </Callout>
                )}
                {stats.best && <Callout t="Most balanced day" cls="good"><b>{stats.best.lab}</b> — {stats.best.r} men per 100 women.</Callout>}
                {stats.skew && (
                  <Callout t="Most skewed day" cls="leak">
                    <b>{stats.skew.lab}</b> — {stats.skew.r} men per 100 women. Watch what drove it (campaigns, push sends).
                  </Callout>
                )}
                {stats.hasBoth && (
                  <Callout t="Weekend effect">
                    Weekends average <b>{fmt(stats.wkendAvg)}</b> actives vs <b>{fmt(stats.wkdayAvg)}</b> on weekdays
                    {stats.wkdayAvg ? ` (${Math.round(((stats.wkendAvg - stats.wkdayAvg) / stats.wkdayAvg) * 100)}% lift).` : '.'}
                  </Callout>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Box>
  );
};


// Stacked daily bar chart. Heights are px against a fixed plot area so the grid lines,
// the bars and the max label all agree.
const H = 254;
// Shortest segment that can still carry its count legibly (10.5px text + breathing room).
const SEG_LABEL_MIN = 18;
// Narrowest bar that can hold a count without the digits being clipped by the segment's
// overflow:hidden. Whether a number fits is a question about BAR WIDTH, not about how many
// days are in the window — 30 bars are roomy on a wide screen and cramped on a laptop — so
// measure the plot and derive the width instead of gating on the day count.
const SEG_LABEL_MIN_W = 20;
const CHART_GAP = 6; // keep in sync with .favor-da .chart{gap}

// Live width of an element, tracked across resizes.
function useWidth(ref) {
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setW(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

function DayChart({ stats }) {
  const { rows, maxT, n } = stats;
  const chartRef = useRef(null);
  const chartW = useWidth(chartRef);
  // A long window ("since launch" = 274 bars) can't afford the roomy default gap, and the
  // bars must be allowed to shrink past .day's 8px min-width — at 8px + 6px gap the row needs
  // ~3,800px and simply runs off the card. `dense` drops both so the plot always fits, and
  // narrows the gap further the longer the window gets.
  const gap = n > 120 ? 1 : n > 40 ? 2 : CHART_GAP;
  const dense = gap !== CHART_GAP;
  // What each bar actually gets: the plot minus the inter-bar gaps, split n ways.
  const barW = n && chartW ? (chartW - gap * (n - 1)) / n : 0;
  const showTot = barW >= SEG_LABEL_MIN_W;
  const labEvery = n > 16 ? Math.ceil(n / 10) : 1;
  const px = (v) => (maxT && v ? Math.max(Math.round((v / maxT) * H), 2) : 0);
  return (
    <div className="chartwrap">
      <span className="ymax">{fmt(maxT)} max</span>
      <div className="grid">{[0.25, 0.5, 0.75].map((f) => <i key={f} style={{ bottom: f * H + 26 }} />)}</div>
      <div className={`chart${dense ? ' dense' : ''}`} ref={chartRef} style={{ gap }}>
        {rows.map((r, i) => (
          <div
            key={r.date}
            className={`day${r.wkend ? ' wkend' : ''}`}
            title={`${r.dow} ${r.lab} — ${fmt(r.total)} active (${fmt(r.men)} M · ${fmt(r.women)} W${r.women ? ` · ${r.r}/100` : ''})`}
          >
            {showTot ? <span className="tot">{fmt(r.total)}</span> : null}
            {/* The per-gender count only goes INSIDE its block when the block is tall enough
                to hold the text — below that it would spill over the neighbouring segment.
                Women are the short segment on nearly every bar, so when theirs can't hold the
                number it is lifted OUT and pinned just above the block rather than dropped. */}
            <div className="seg m" style={{ height: px(r.men) }}>
              {showTot && px(r.men) >= SEG_LABEL_MIN ? <span className="sv">{fmt(r.men)}</span> : null}
            </div>
            <div className="seg w" style={{ height: px(r.women) }}>
              {showTot && px(r.women) >= SEG_LABEL_MIN ? <span className="sv">{fmt(r.women)}</span> : null}
            </div>
            {showTot && r.women && px(r.women) < SEG_LABEL_MIN ? (
              <span className="sv out" style={{ bottom: px(r.women) + 1 }}>{fmt(r.women)}</span>
            ) : null}
            {i % labEvery === 0 || i === n - 1 ? <span className="dl">{r.lab}</span> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

const HH = 224;
const HSEG_LABEL_MIN = 17;
const HOURS_GAP = 4; // keep in sync with .favor-da .chart.hours{gap}
function HourChart({ hourly, avg }) {
  const { rows, max, peak } = hourly;
  const chartRef = useRef(null);
  const chartW = useWidth(chartRef);
  const barW = chartW ? (chartW - HOURS_GAP * (rows.length - 1)) / rows.length : 0;
  const fits = barW >= SEG_LABEL_MIN_W;
  const px = (v) => (max && v ? Math.max(Math.round((v / max) * HH), 2) : 0);
  return (
    <div className="chartwrap">
      <span className="ymax">{fmt(max)} max</span>
      <div className="grid">{[0.25, 0.5, 0.75].map((f) => <i key={f} style={{ bottom: f * HH + 26 }} />)}</div>
      <div className="chart hours" ref={chartRef}>
        {rows.map((r) => (
          <div
            key={r.hour}
            className={`day${r.hour === peak.hour ? ' pk' : ''}`}
            title={`${hRange(r.hour)} — ${fmt(r.total)}${avg ? ' avg' : ''} active (${fmt(r.men)} M · ${fmt(r.women)} W${r.women ? ` · ${ratioOf(r.women, r.men)}/100` : ''})`}
          >
            {r.hour === peak.hour ? <span className="tot">{fmt(r.total)}</span> : null}
            <div className="seg m" style={{ height: px(r.men) }}>
              {fits && px(r.men) >= HSEG_LABEL_MIN ? <span className="sv">{fmt(r.men)}</span> : null}
            </div>
            <div className="seg w" style={{ height: px(r.women) }}>
              {fits && px(r.women) >= HSEG_LABEL_MIN ? <span className="sv">{fmt(r.women)}</span> : null}
            </div>
            {fits && r.women && px(r.women) < HSEG_LABEL_MIN ? (
              <span className="sv out" style={{ bottom: px(r.women) + 1 }}>{fmt(r.women)}</span>
            ) : null}
            {r.hour % 3 === 0 || r.hour === 23 ? <span className="dl">{h12(r.hour)}</span> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function DayRows({ stats }) {
  const { rows, maxGender, best, skew } = stats;
  const w = (v) => (maxGender ? `${Math.max((v / maxGender) * 100, v ? 1 : 0)}%` : 0);
  return (
    <div className="drows">
      <div className="drow hd"><span>Date</span><span>Women / Men</span><span className="r">Total</span><span className="c">M per 100 W</span></div>
      {rows.slice().reverse().map((r) => {
        const tag = ratioTag(r.r);
        const cls = best && r.date === best.date ? ' best' : skew && r.date === skew.date ? ' skew' : '';
        return (
          <div className={`drow${cls}`} key={r.date}>
            <div className="dd">
              <span className="dt">{r.lab}</span>
              <span className={`dw${r.wkend ? ' we' : ''}`}>{r.dow}{r.wkend ? ' · weekend' : ''}</span>
            </div>
            <div className="dbars">
              <div className="db w"><div className="tr"><div className="fl" style={{ width: w(r.women) }} /></div><span className="nn">{fmt(r.women)} <i>W</i></span></div>
              <div className="db m"><div className="tr"><div className="fl" style={{ width: w(r.men) }} /></div><span className="nn">{fmt(r.men)} <i>M</i></span></div>
            </div>
            <div className="dtot serif">{fmt(r.total)}</div>
            <div className={`dratio ${r.women ? tag.cls : ''}`}>{r.women ? `${r.r} / 100` : '—'}</div>
          </div>
        );
      })}
    </div>
  );
}

const STYLES = `
.favor-da{--ink:#25313f;--ink-2:#3f4854;--muted:#7c8590;--faint:#9aa2ac;--line:#ececec;--line-2:#e3e3e3;--wash:#f4f2ef;--red:#e15241;--red-d:#d43f2d;--green:#2f8f5b;--gold:#c08a2e;--gold-b:#d9a441;--blue:#3f6fd8;
  --w1:#e79ac0;--w2:#d76aa8;--m1:#7ea8e6;--m2:#3f6fd8;
  --serif:"ivypresto-headline",Georgia,serif;--sans:"indivisible","Neue Montreal","Helvetica Neue",Arial,sans-serif;
  --card-sh:0 1px 2px rgba(20,25,35,.04),0 6px 18px -10px rgba(20,25,35,.10);
  color:var(--ink-2);font-family:var(--sans);font-size:14px;line-height:1.5;-webkit-font-smoothing:antialiased}
.favor-da *{box-sizing:border-box;margin:0;padding:0}
.favor-da .serif{font-family:var(--serif)}
.favor-da .da-center{min-height:360px;display:flex;align-items:center;justify-content:center}
.favor-da .filters{display:flex;flex-wrap:wrap;gap:20px 26px;align-items:center;margin:8px 0 4px}
.favor-da .fg{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
.favor-da .fg>.lab{font-size:11px;font-weight:700;letter-spacing:.12em;color:var(--faint);text-transform:uppercase}
.favor-da .da-export{margin-left:auto;display:inline-flex;align-items:center;gap:7px;background:#14181c;border:0;border-radius:10px;padding:9px 15px;font-family:inherit;font-size:12.5px;font-weight:600;color:#fff;cursor:pointer;transition:.12s}
.favor-da .da-export:hover{background:#000}
.favor-da .da-export:disabled{opacity:.45;cursor:default}
.favor-da .da-export svg{display:block}
.favor-da .da-pills{display:inline-flex;background:var(--wash);border:1px solid var(--line);border-radius:11px;padding:4px}
.favor-da .da-pill{border:0;background:transparent;font-family:inherit;font-size:12.5px;font-weight:600;color:var(--ink-2);padding:7px 13px;border-radius:8px;cursor:pointer;white-space:nowrap;transition:.12s}
.favor-da .da-pill:hover{color:var(--ink)}
.favor-da .da-pill.on{background:#fff;color:var(--ink);box-shadow:0 1px 3px rgba(0,0,0,.12)}
.favor-da .da-pill.on.red{background:var(--red);color:#fff;box-shadow:0 2px 8px -2px rgba(225,82,65,.5)}
.favor-da .da-pill.on.black{background:#14181c;color:#fff}
.favor-da .note{font-size:12px;color:var(--muted);line-height:1.5;margin:14px 0 4px;font-style:italic}
.favor-da .note b{color:var(--ink-2);font-style:normal}
.favor-da .flag{display:inline-flex;gap:7px;align-items:center;font-style:normal;font-size:11px;font-weight:600;color:#8a6d1f;background:#fdf6e6;border:1px solid #f0e2bd;padding:3px 9px;border-radius:7px;margin-left:6px}
.favor-da .daterange{display:inline-flex;align-items:center;gap:8px}
.favor-da .daterange input[type=date]{font-family:inherit;font-size:12.5px;font-weight:600;color:var(--ink-2);border:1px solid var(--line-2);border-radius:10px;padding:8px 11px;background:#fff;cursor:pointer}
.favor-da .dr-sep{font-size:12px;color:var(--muted)}

.favor-da .kpis{display:grid;grid-template-columns:repeat(5,1fr);gap:13px;margin:18px 0 24px}
.favor-da .kpi{border:1px solid var(--line);border-radius:14px;padding:15px 17px;box-shadow:var(--card-sh);background:#fff}
/* A KPI rendered as a <button> inherits the UA's button chrome (centred text, its own
   font) — reset it back to the tile the div version already is. */
.favor-da .kpi.tap{display:block;width:100%;text-align:left;font-family:inherit;font-size:inherit;color:inherit;cursor:pointer;transition:transform .12s,box-shadow .12s}
.favor-da .kpi.tap:hover{transform:translateY(-2px);box-shadow:0 2px 4px rgba(20,25,35,.06),0 12px 24px -12px rgba(20,25,35,.22)}
.favor-da .kpi.tap:focus-visible{outline:2px solid var(--green);outline-offset:2px}
/* Static, not hover-revealed: these tiles are narrow enough that an absolutely-positioned
   hint would sit on top of the men/women line, and a click affordance nobody can see until
   they happen to hover isn't one. */
.favor-da .kpi .kcta{display:block;margin-top:9px;font-size:10.5px;font-weight:700;color:var(--green)}
.favor-da .kpi.tap:hover .kcta{text-decoration:underline}
.favor-da .kpi .k{font-size:10.5px;font-weight:700;letter-spacing:.05em;color:var(--faint);text-transform:uppercase;margin-bottom:8px;display:flex;align-items:center;gap:8px}
.favor-da .kpi .v{font-weight:700;font-size:26px;line-height:1;color:var(--ink);letter-spacing:-.01em}
.favor-da .kpi .v small{font-family:var(--sans);font-size:11.5px;font-weight:700;margin-left:6px;color:var(--muted)}
.favor-da .kpi .d{font-size:11.5px;color:var(--muted);margin-top:7px;line-height:1.35}
.favor-da .kpi.w{background:linear-gradient(180deg,#fdf3f8,#fff);border-color:#f6d8e7}.favor-da .kpi.w .v{color:#b23c7e}
.favor-da .kpi.m{background:linear-gradient(180deg,#f1f5fd,#fff);border-color:#d9e4f6}.favor-da .kpi.m .v{color:#2d5bb8}
.favor-da .kpi.live{background:linear-gradient(180deg,#f2faf5,#fff);border-color:#dcece2}.favor-da .kpi.live .v{color:var(--green)}
.favor-da .pulse{width:9px;height:9px;border-radius:50%;background:var(--green);position:relative;flex-shrink:0}
.favor-da .pulse::after{content:"";position:absolute;inset:-4px;border-radius:50%;border:2px solid var(--green);opacity:.5;animation:da-pu 1.6s ease-out infinite}
@keyframes da-pu{0%{transform:scale(.5);opacity:.7}80%{transform:scale(1.25);opacity:0}100%{opacity:0}}
.favor-da .rtag{display:inline-block;font-size:10.5px;font-weight:700;padding:3px 9px;border-radius:20px;margin-top:8px}
.favor-da .rtag.ok{color:var(--green);background:#eaf6ef}
.favor-da .rtag.mid{color:#8a6d1f;background:#fdf6e6}
.favor-da .rtag.bad{color:var(--red-d);background:#fdeeea}

.favor-da .sec{border:1px solid var(--line);border-radius:16px;padding:20px 22px;box-shadow:var(--card-sh);margin-bottom:18px;background:#fff}
.favor-da .sec-h{display:flex;align-items:baseline;justify-content:space-between;gap:16px;margin-bottom:6px;flex-wrap:wrap}
.favor-da .sec-h h2{font-family:var(--sans);font-weight:600;font-size:24px !important;line-height:1.3;color:var(--ink)}
.favor-da .sec-h .hint{font-size:12px;color:var(--muted);font-style:italic}
.favor-da .legend{display:flex;gap:18px;align-items:center;flex-wrap:wrap;font-size:12px;color:var(--muted);margin:10px 0 4px}
.favor-da .legend .lg{display:inline-flex;align-items:center;gap:7px;font-weight:600}
.favor-da .legend .faint{color:var(--faint)}
.favor-da .legend .dotw{width:12px;height:12px;border-radius:4px;background:linear-gradient(90deg,var(--w1),var(--w2))}
.favor-da .legend .dotm{width:12px;height:12px;border-radius:4px;background:linear-gradient(90deg,var(--m1),var(--m2))}

/* padding-top reserves two clear bands above the plot: the ymax label on top, then the
   per-bar total labels (which hang 19px above each bar). Without it the tallest bar —
   whose top touches the plot ceiling — paints its segment and its total over the ymax. */
.favor-da .chartwrap{position:relative;margin-top:18px;padding-top:40px}
.favor-da .ymax{position:absolute;top:0;left:0;font-size:11px;font-weight:700;color:var(--faint)}
.favor-da .grid{position:absolute;left:0;right:0;top:0;bottom:26px;pointer-events:none}
.favor-da .grid i{position:absolute;left:0;right:0;border-top:1px dashed #eeeae4}
.favor-da .chart{display:flex;align-items:flex-end;gap:6px;height:280px;padding-bottom:26px;position:relative}
.favor-da .day{flex:1;display:flex;flex-direction:column;justify-content:flex-end;align-items:stretch;position:relative;min-width:8px}
/* Long windows: bars must be free to shrink below the 8px min-width, or 274 of them overflow
   the card. min-width:0 lets flex do its job; the chart itself can never spill regardless. */
.favor-da .chart{max-width:100%}
.favor-da .chart.dense .day{min-width:0}
.favor-da .chart.dense .seg.m{border-radius:2px 2px 0 0}
.favor-da .chart.dense .seg.w{border-radius:0 0 1px 1px}
.favor-da .day .tot{position:absolute;top:-19px;left:50%;transform:translateX(-50%);font-size:10.5px;font-weight:700;color:var(--ink-2);white-space:nowrap}
.favor-da .seg{width:100%;display:flex;align-items:center;justify-content:center;overflow:hidden}
.favor-da .seg .sv{font-size:10.5px;font-weight:700;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,.28);line-height:1;pointer-events:none}
/* Women's count when its block is too short to hold it: a chip pinned just above the block,
   pink on white so it still reads as the women's number and not part of the men's segment. */
.favor-da .day .sv.out{position:absolute;left:50%;transform:translateX(-50%);z-index:1;font-size:10px;font-weight:700;line-height:1;color:var(--w2);background:#fff;border-radius:4px;padding:1px 3px;box-shadow:0 0 0 1px rgba(215,106,168,.35);text-shadow:none;white-space:nowrap;pointer-events:none}
.favor-da .seg.m{background:linear-gradient(180deg,var(--m1),var(--m2));border-radius:6px 6px 0 0}
.favor-da .seg.w{background:linear-gradient(180deg,var(--w1),var(--w2));border-radius:0 0 3px 3px}
.favor-da .day .dl{position:absolute;bottom:-24px;left:50%;transform:translateX(-50%);font-size:10.5px;font-weight:600;color:var(--faint);white-space:nowrap}
.favor-da .day.wkend .dl{color:var(--gold)}
.favor-da .day:hover .seg{filter:brightness(1.06)}
.favor-da .chart.hours{gap:4px;height:250px}
.favor-da .chart.hours .day{border-radius:8px}
.favor-da .chart.hours .day.pk{background:#fbf6ec}
.favor-da .chart.hours .day.pk .tot{color:var(--gold)}
.favor-da .chart.hours .day.pk .dl{color:var(--gold);font-weight:700}
.favor-da .chart.hours .day .dl{font-size:10px}

.favor-da .drows{margin-top:18px}
.favor-da .drow{display:grid;grid-template-columns:150px 1fr 96px 118px;align-items:center;gap:16px;padding:9px 12px;border-radius:12px}
.favor-da .drow+.drow{margin-top:3px}
.favor-da .drow.hd{padding:4px 12px}
.favor-da .drow.hd span{font-size:10.5px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:var(--faint)}
.favor-da .drow.hd .r{text-align:right}.favor-da .drow.hd .c{text-align:center}
.favor-da .drow.best{background:#f2faf5;box-shadow:inset 3px 0 0 var(--green)}
.favor-da .drow.skew{background:#fdf5f3;box-shadow:inset 3px 0 0 var(--red)}
.favor-da .dd{display:flex;flex-direction:column;gap:1px}
.favor-da .dd .dt{font-size:13px;font-weight:600;color:var(--ink-2)}
.favor-da .dd .dw{font-size:10.5px;color:var(--faint);font-weight:600}
.favor-da .dd .dw.we{color:var(--gold)}
.favor-da .dbars{display:flex;flex-direction:column;gap:5px}
.favor-da .db{display:grid;grid-template-columns:1fr 74px;align-items:center;gap:9px}
.favor-da .db .tr{position:relative;height:14px;background:#f1efeb;border-radius:5px;overflow:hidden}
.favor-da .db .fl{position:absolute;inset:0 auto 0 0;border-radius:5px;transition:width .5s cubic-bezier(.4,0,.2,1)}
.favor-da .db.w .fl{background:linear-gradient(90deg,var(--w1),var(--w2))}
.favor-da .db.m .fl{background:linear-gradient(90deg,var(--m1),var(--m2))}
.favor-da .db .nn{font-size:11.5px;font-weight:700;color:var(--ink-2);text-align:right;white-space:nowrap}
.favor-da .db .nn i{font-style:normal;color:var(--faint);font-weight:600}
.favor-da .dtot{font-weight:700;font-size:18px;color:var(--ink);text-align:right}
.favor-da .dratio{font-size:11.5px;font-weight:700;text-align:center;border-radius:8px;padding:7px 6px;background:#f4f2ef;color:var(--muted)}
.favor-da .dratio.ok{color:var(--green);background:#eaf6ef}
.favor-da .dratio.mid{color:#8a6d1f;background:#fdf6e6}
.favor-da .dratio.bad{color:var(--red-d);background:#fdeeea}

.favor-da .ceta{display:flex;gap:12px;flex-wrap:wrap;margin-top:18px}
.favor-da .callout{flex:1;min-width:230px;border:1px solid var(--line);border-radius:12px;padding:13px 16px;background:#fafafa}
.favor-da .callout .ct{font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--faint);margin-bottom:6px}
.favor-da .callout .cv{font-size:12.5px;color:var(--ink-2);line-height:1.5}
.favor-da .callout .cv b{color:var(--ink)}
.favor-da .callout.leak{background:#fdf3f1;border-color:#f7d9d2}.favor-da .callout.leak .cv b{color:var(--red-d)}
.favor-da .callout.good{background:#f2faf5;border-color:#d9ece1}.favor-da .callout.good .cv b{color:var(--green)}

@media(max-width:960px){.favor-da .kpis{grid-template-columns:repeat(2,1fr)}}
@media(max-width:820px){.favor-da .drow{grid-template-columns:104px 1fr 64px 92px;gap:10px}.favor-da .day .tot{display:none}.favor-da .seg .sv,.favor-da .day .sv.out{display:none}.favor-da .filters{gap:16px}}
`;

export default DailyActives;
